import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { ReasonSchema } from './catalogue.js';
import { problemResponse } from './common.js';
import { CustomerStatusSchema, CustomerTypeSchema } from './customers.js';

/** Pricing (spec §4, plan 03): quotes, price lists, partner / personal discounts, promotions. */

const kopecks = z.number().int().min(0).max(100_000_000);
const day = z.iso.date().meta({ description: 'Kyiv calendar date, inclusive' });
const optionalDay = day.nullable().optional();

export const TierCodeSchema = z.enum(['retail', 'b2b']).meta({ id: 'TierCode' });
export const DiscountModeSchema = z.enum(['percent_off', 'fixed_price']).meta({
  id: 'DiscountMode',
  description: '`value` is percent for percent_off, kopecks for fixed_price',
});
export const PromotionScopeSchema = z
  .enum(['all', 'category', 'product'])
  .meta({ id: 'PromotionScope' });
export const WindowStateSchema = z
  .enum(['scheduled', 'active', 'ended'])
  .meta({ id: 'WindowState', description: 'Where the validity window stands today (Kyiv)' });

const percentOrKopecks = z
  .number()
  .min(0)
  .max(100_000_000)
  .meta({ description: 'Percent (up to 2 decimals) or kopecks, depending on the mode' });

// ---------- Quotes ----------

export const QuoteLineRequestSchema = z.object({
  sku: z.string().trim().min(1).max(64),
  qty: z.number().int().min(1).max(10_000),
  override: z
    .object({
      mode: z.enum(['price', 'percent']),
      value: percentOrKopecks.meta({ description: 'Kopecks for price, percent for percent' }),
      reason: ReasonSchema,
    })
    .nullable()
    .optional()
    .meta({ description: 'Manager override (needs orders.override); never sent by the store' }),
});

export const QuoteRequestSchema = z
  .object({
    customerId: z.uuid().nullable().optional().meta({ description: 'Omit or null for a guest' }),
    customerType: CustomerTypeSchema.optional().meta({ description: 'Guests only; default b2c' }),
    lines: z.array(QuoteLineRequestSchema).min(1).max(200),
    pointsRequested: z.number().int().min(0).optional(),
  })
  .meta({ id: 'QuoteRequest' });

export const PriceBreakdownSchema = z
  .object({
    base: kopecks,
    tier: z.object({
      tier: TierCodeSchema,
      rule_id: z.string().nullable(),
      scope: z.enum(['product', 'category', 'tier']).nullable(),
      mode: z.enum(['fixed_price', 'percent_off_base', 'markup_on_cost']).nullable(),
      value: z.number().nullable(),
      result: kopecks,
    }),
    personal: z
      .object({
        discount_id: z.string(),
        scope: z.enum(['product', 'category', 'customer']),
        mode: DiscountModeSchema,
        value: z.number(),
        result: kopecks,
      })
      .nullable(),
    promo: z
      .object({
        promotion_id: z.string(),
        name: z.string(),
        scope: PromotionScopeSchema,
        mode: DiscountModeSchema,
        value: z.number(),
        stackable: z.boolean(),
        result: kopecks,
      })
      .nullable(),
    override: z
      .object({ mode: z.enum(['price', 'percent']), value: z.number(), result: kopecks })
      .nullable(),
    unit_price: kopecks,
    engine_version: z.string(),
  })
  .meta({
    id: 'PriceBreakdown',
    description: 'Spec §4 breakdown snapshot (kopecks; value in percent or kopecks)',
  });

export const PricedLineSchema = z
  .object({
    productId: z.uuid(),
    sku: z.string(),
    name: z.string(),
    qty: z.number().int(),
    unitPrice: kopecks,
    lineTotal: z.number().int(),
    breakdown: PriceBreakdownSchema,
  })
  .meta({ id: 'PricedLine' });

const pricedCart = {
  customerId: z.uuid().nullable(),
  customerType: CustomerTypeSchema,
  tier: TierCodeSchema,
  lines: z.array(PricedLineSchema),
  subtotal: z.number().int(),
  pointsRequested: z.number().int(),
  maxRedeemablePoints: z.number().int(),
  pointsApplied: z.number().int(),
  pointsDiscount: z
    .number()
    .int()
    .meta({ description: 'One order-level discount, never per line' }),
  total: z.number().int(),
  pointsToEarn: z.number().int().meta({
    description: 'Q-points the order would earn when paid; 0 for B2B or with the program off',
  }),
  engineVersion: z.string(),
};

export const QuoteSchema = z
  .object({ quoteId: z.uuid(), expiresAt: z.iso.datetime(), ...pricedCart })
  .meta({ id: 'Quote' });

export const PricePreviewSchema = z.object(pricedCart).meta({ id: 'PricePreview' });

// ---------- Discounts and promotions ----------

const target = {
  productId: z.uuid().nullable().optional(),
  categoryId: z.uuid().nullable().optional(),
};

export const DiscountInputSchema = z
  .object({
    ...target,
    mode: DiscountModeSchema,
    value: percentOrKopecks,
    validFrom: optionalDay,
    validTo: optionalDay,
  })
  .meta({ id: 'DiscountInput', description: 'Neither productId nor categoryId = the whole range' });

export const CreateDiscountSchema = DiscountInputSchema.extend({
  customerId: z.uuid(),
  reason: ReasonSchema,
}).meta({ id: 'CreateDiscount' });

export const UpdateDiscountSchema = DiscountInputSchema.extend({ reason: ReasonSchema }).meta({
  id: 'UpdateDiscount',
});

export const DeleteWithReasonSchema = z
  .object({ reason: ReasonSchema })
  .meta({ id: 'DeleteWithReason' });

const labels = {
  productSku: z.string().nullable(),
  productName: z.string().nullable(),
  categoryPath: z.string().nullable(),
};

export const DiscountSchema = z
  .object({
    id: z.uuid(),
    customerId: z.uuid(),
    customerName: z.string().nullable(),
    customerType: CustomerTypeSchema.nullable(),
    customerStatus: CustomerStatusSchema.nullable().meta({
      description: 'A blocked customer gets no prices; a pending one buys at retail',
    }),
    productId: z.uuid().nullable(),
    categoryId: z.uuid().nullable(),
    ...labels,
    mode: DiscountModeSchema,
    value: z.number(),
    validFrom: day.nullable(),
    validTo: day.nullable(),
    state: WindowStateSchema,
    reason: z.string(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'Discount' });

export const DiscountPageSchema = z
  .object({ items: z.array(DiscountSchema), total: z.number().int() })
  .meta({ id: 'DiscountPage' });

export const DiscountQuerySchema = z.object({
  customerId: z.uuid().optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export const PromotionInputSchema = z
  .object({
    name: z.string().max(200),
    scope: PromotionScopeSchema,
    ...target,
    mode: DiscountModeSchema,
    value: percentOrKopecks,
    appliesTo: z.array(CustomerTypeSchema).min(1).max(2),
    stackable: z
      .boolean()
      .optional()
      .meta({ description: 'Compounds on the personal price (percent only); otherwise best-of' }),
    validFrom: optionalDay,
    validTo: optionalDay,
  })
  .meta({ id: 'PromotionInput' });

export const SavePromotionSchema = PromotionInputSchema.extend({ reason: ReasonSchema }).meta({
  id: 'SavePromotion',
});

export const PromotionSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    scope: PromotionScopeSchema,
    productId: z.uuid().nullable(),
    categoryId: z.uuid().nullable(),
    ...labels,
    mode: DiscountModeSchema,
    value: z.number(),
    appliesTo: z.array(CustomerTypeSchema),
    stackable: z.boolean(),
    validFrom: day.nullable(),
    validTo: day.nullable(),
    state: WindowStateSchema,
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'Promotion' });

export const PromotionListSchema = z.array(PromotionSchema).meta({ id: 'PromotionList' });

export const PreviewRequestSchema = QuoteRequestSchema.extend({
  draft: z
    .object({
      personalDiscount: DiscountInputSchema.extend({
        id: z.uuid().nullable().optional().meta({ description: 'The discount being edited' }),
      }).optional(),
      promotion: PromotionInputSchema.extend({
        id: z.uuid().nullable().optional().meta({ description: 'The promotion being edited' }),
      }).optional(),
    })
    .optional()
    .meta({
      description: 'Unsaved changes to price with, replacing the saved row with the same id',
    }),
}).meta({ id: 'PreviewRequest' });

// ---------- Price lists ----------

export const PriceListSchema = z
  .object({
    tier: TierCodeSchema,
    customerId: z.uuid().nullable(),
    date: day,
    engineVersion: z.string(),
    items: z.array(
      z.object({
        productId: z.uuid(),
        sku: z.string(),
        name: z.string(),
        basePrice: kopecks,
        price: kopecks,
      }),
    ),
  })
  .meta({ id: 'PriceList' });

export const PriceListQuerySchema = z.object({ tier: TierCodeSchema });

export type QuoteRequest = z.infer<typeof QuoteRequestSchema>;
export type QuoteDto = z.infer<typeof QuoteSchema>;
export type PricePreviewDto = z.infer<typeof PricePreviewSchema>;
export type PricedLineDto = z.infer<typeof PricedLineSchema>;
export type PriceBreakdownDto = z.infer<typeof PriceBreakdownSchema>;
export type PreviewRequest = z.infer<typeof PreviewRequestSchema>;
export type DiscountInput = z.infer<typeof DiscountInputSchema>;
export type CreateDiscountRequest = z.infer<typeof CreateDiscountSchema>;
export type UpdateDiscountRequest = z.infer<typeof UpdateDiscountSchema>;
export type DeleteWithReasonRequest = z.infer<typeof DeleteWithReasonSchema>;
export type DiscountDto = z.infer<typeof DiscountSchema>;
export type DiscountPageDto = z.infer<typeof DiscountPageSchema>;
export type DiscountQuery = z.infer<typeof DiscountQuerySchema>;
export type PromotionInput = z.infer<typeof PromotionInputSchema>;
export type SavePromotionRequest = z.infer<typeof SavePromotionSchema>;
export type PromotionDto = z.infer<typeof PromotionSchema>;
export type PriceListDto = z.infer<typeof PriceListSchema>;
export type PriceListQuery = z.infer<typeof PriceListQuerySchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });
const body = <T extends z.ZodType>(schema: T) => ({ required: true, content: json(schema) });
const errors = {
  '400': problemResponse('Invalid input'),
  '401': problemResponse('Not signed in'),
  '403': problemResponse('Missing permission'),
  '404': problemResponse('Not found'),
};
const id = { path: z.object({ id: z.uuid() }) };

export const pricingPaths: ZodOpenApiPathsObject = {
  '/api/v1/pricing/quote': {
    post: {
      summary:
        'Price a cart and keep the quote for 30 minutes (needs orders.edit). The only side effect is the stored quote',
      requestBody: body(QuoteRequestSchema),
      responses: {
        '201': { description: 'Quoted', content: json(QuoteSchema) },
        ...errors,
        '422': problemResponse('Customer is blocked'),
      },
    },
  },
  '/api/v1/pricing/preview': {
    post: {
      summary:
        'Price a sample cart for a customer, optionally with unsaved discount changes (needs pricing.edit). Nothing is stored',
      requestBody: body(PreviewRequestSchema),
      responses: {
        '200': { description: 'Priced', content: json(PricePreviewSchema) },
        ...errors,
        '422': problemResponse('Customer is blocked'),
      },
    },
  },
  '/api/v1/pricing/price-list': {
    get: {
      summary: 'Resolved unit prices of every active SKU for a tier (guests)',
      requestParams: { query: PriceListQuerySchema },
      responses: { '200': { description: 'OK', content: json(PriceListSchema) }, ...errors },
    },
  },
  '/api/v1/pricing/customer-prices/{id}': {
    get: {
      summary: "Resolved unit prices of every active SKU for one customer (a partner's own prices)",
      requestParams: id,
      responses: {
        '200': { description: 'OK', content: json(PriceListSchema) },
        ...errors,
        '422': problemResponse('Customer is blocked'),
      },
    },
  },
  '/api/v1/pricing/discounts': {
    get: {
      summary:
        'Partner / personal discounts, newest change first (pricing.edit or customers.manage)',
      requestParams: { query: DiscountQuerySchema },
      responses: { '200': { description: 'OK', content: json(DiscountPageSchema) }, ...errors },
    },
    post: {
      summary: 'Add a discount for a customer (reason required, audited)',
      requestBody: body(CreateDiscountSchema),
      responses: {
        '201': { description: 'Created', content: json(DiscountSchema) },
        ...errors,
        '409': problemResponse(
          'The customer already has a discount for this target on these dates',
        ),
      },
    },
  },
  '/api/v1/pricing/discounts/{id}': {
    put: {
      summary: 'Change a discount (reason required, audited)',
      requestParams: id,
      requestBody: body(UpdateDiscountSchema),
      responses: {
        '200': { description: 'Saved', content: json(DiscountSchema) },
        ...errors,
        '409': problemResponse(
          'The customer already has a discount for this target on these dates',
        ),
      },
    },
    delete: {
      summary: 'Remove a discount (reason required, audited)',
      requestParams: id,
      requestBody: body(DeleteWithReasonSchema),
      responses: { '204': { description: 'Removed' }, ...errors },
    },
  },
  '/api/v1/pricing/promotions': {
    get: {
      summary: 'All promotions, newest first',
      responses: { '200': { description: 'OK', content: json(PromotionListSchema) }, ...errors },
    },
    post: {
      summary: 'Add a promotion (reason required, audited)',
      requestBody: body(SavePromotionSchema),
      responses: { '201': { description: 'Created', content: json(PromotionSchema) }, ...errors },
    },
  },
  '/api/v1/pricing/promotions/{id}': {
    put: {
      summary: 'Change a promotion (reason required, audited)',
      requestParams: id,
      requestBody: body(SavePromotionSchema),
      responses: { '200': { description: 'Saved', content: json(PromotionSchema) }, ...errors },
    },
    delete: {
      summary: 'Remove a promotion (reason required, audited)',
      requestParams: id,
      requestBody: body(DeleteWithReasonSchema),
      responses: { '204': { description: 'Removed' }, ...errors },
    },
  },
};
